import { createRoute } from '@tanstack/react-router';
import { rootRoute } from '@trashlab/sitesapp/src/routes/__root';
import { DumpsterTypes } from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/dumpster-rentals/dumpster-types';
import { DumpsterHeroSection } from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/dumpster-rentals/dumpster-hero-section';
import { MaterialGuide } from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/dumpster-faq/material-guide';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/denversiteworks/contexts/use-tenant-context';
import { FAQ } from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/shared/faq-section';
import CtaSection from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/shared/cta-section';
import Testimonials from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/shared/testimonials-section';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/denversiteworks/lib/utils/get-variant-value';
import RentalSteps from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/dumpster-rentals/rental-steps';
import { DumpsterMapRates } from '@trashlab/sitesapp/src/organizations/denversiteworks/template/components/dumpster-rentals/dumpster-map-rates';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';
import {
  getDumpsterType,
  type DumpsterType,
  useDumpsterTypeSearch,
} from '@trashlab/sitesapp/src/lib/dumpster-rentals';

const DumpsterRentalsPage = () => {
  const type = useDumpsterTypeSearch();
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address as any);
  const city = orgAddress?.city ?? 'San Francisco';
  const state = orgAddress?.state ?? 'CA';

  const residentialHeroSection = {
    title: 'Residential dumpster rental',
    content: useVariantValue(
      'RESIDENTIAL_HERO_SUBTITLE',
      `A residential dumpster rental from ${
        website?.organization?.name || 'our company'
      } makes all of this possible. Tackle that long-delayed remodeling task or swiftly remove storm debris from your property. ${
        website?.organization?.name || 'our company'
      } is a ${city}, ${state} Authorized Waste Hauler AWH#2024-0001`,
    ),
    heroImage: useVariantValue(
      'RESIDENTIAL_IMAGE',
      '/default/residential-dumpster.svg',
    ),
    dataVariable: 'RESIDENTIAL_HERO_SUBTITLE',
    imageDataVariable: 'RESIDENTIAL_IMAGE',
  };

  const commercialHeroSection = {
    title: 'Commercial dumpster rental',
    content: useVariantValue(
      'COMMERCIAL_HERO_SUBTITLE',
      `${
        website?.organization?.name || 'Our company'
      } provides a wide array of commercial dumpster rental solutions tailored to help solve your business needs. Our selection of roll off dumpster rentals spans a broad spectrum of sizes. ${
        website?.organization?.name || 'our company'
      } is a ${city}, ${state} Authorized Waste Hauler AWH#2024-0001`,
    ),
    heroImage: useVariantValue(
      'COMMERCIAL_IMAGE',
      '/default/commercial-dumpster.svg',
    ),
    dataVariable: 'COMMERCIAL_HERO_SUBTITLE',
    imageDataVariable: 'COMMERCIAL_IMAGE',
  };

  const rolloffHeroSection = {
    title: 'Rolloff dumpster rental',
    content: useVariantValue(
      'ROLLOFF_HERO_SUBTITLE',
      `Construction and large project dumpster rental from ${
        website?.organization?.name || 'our company'
      } makes all of this possible. Tackle that extensive renovation project or swiftly remove demolition debris from your site. No matter the construction task, rely on us for a cost-effective dumpster rental that fits perfectly into your schedule. ${
        website?.organization?.name || 'our company'
      } is a ${city}, ${state} Authorized Waste Hauler AWH#2024-0001`,
    ),
    heroImage: useVariantValue(
      'ROLLOFF_IMAGE',
      '/default/rolloff-dumpster.svg',
    ),
    dataVariable: 'ROLLOFF_HERO_SUBTITLE',
    imageDataVariable: 'ROLLOFF_IMAGE',
  };

  const portableToiletHeroSection = {
    title: 'Portable toilet rental',
    content: useVariantValue(
      'PORTABLE_TOILET_HERO_SUBTITLE',
      `${
        website?.organization?.name || 'Our company'
      } provides convenient and clean portable toilet solutions for events, construction sites, and more. Our portable restrooms are regularly serviced and maintained to ensure the highest standards of cleanliness and comfort. ${
        website?.organization?.name || 'our company'
      } is a ${city}, ${state} Authorized Waste Hauler AWH#2024-0001`,
    ),
    heroImage: useVariantValue(
      'PORTABLE_TOILET_IMAGE',
      '/default/portable-toilet.svg',
    ),
    dataVariable: 'PORTABLE_TOILET_HERO_SUBTITLE',
    imageDataVariable: 'PORTABLE_TOILET_IMAGE',
  };

  const getHeroSection = () => {
    switch (type) {
      case 'rolloff':
        return rolloffHeroSection;
      case 'commercial':
        return commercialHeroSection;
      case 'portableToilet':
        return portableToiletHeroSection;
      case 'residential':
      default:
        return residentialHeroSection;
    }
  };

  const getFaqSection = () => {
    switch (type) {
      case 'rolloff':
        return (
          <>
            <Testimonials />
            <FAQ
              accordionOnly
              withBackground
              faqType="rolloff"
              customSubtitle="Need help finding the right size dumpster?"
              customTitle="Rolloff dumpster rentals FAQ"
              customDescription="Find all you need to know about our rolloff dumpster rental services in this FAQ section."
            />
          </>
        );
      case 'commercial':
        return (
          <FAQ
            accordionOnly
            faqType="commercial"
            customSubtitle="Commercial & business dumpster rental questions"
            customTitle="Commercial dumpster rentals FAQ"
            customDescription={`Explore ${website?.organization?.name}'s commercial dumpster rental FAQ to find answers to all your questions about dumpster sizes, rental processes, and more. Discover essential information to streamline your commercial dumpster rental experience`}
          />
        );
      case 'portableToilet':
        return (
          <FAQ
            accordionOnly
            faqType="portableToilet"
            customSubtitle="Portable toilet rental questions"
            customTitle="Portable toilet rentals FAQ"
            customDescription="Our FAQ section provides key details about our portable toilet rental services, answering common questions and offering guidance for renting portable restrooms."
          />
        );
      case 'residential':
      default:
        return (
          <FAQ
            accordionOnly
            faqType="residential"
            customSubtitle={`${
              website?.organization?.name || "Our company's"
            } residential dumpsters`}
            customTitle="Residential dumpster rentals FAQ"
            customDescription="Our FAQ section provides key details about our residential dumpster rental services, answering common questions and offering guidance for renting a dumpster."
          />
        );
    }
  };

  const hero = getHeroSection();

  return (
    <div key={type}>
      <DumpsterHeroSection
        title={hero.title}
        content={hero.content}
        heroImage={hero.heroImage}
        contentDataVariable={hero.dataVariable}
        imageDataVariable={hero.imageDataVariable}
      />
      <DumpsterTypes defaultType={type} />
      <DumpsterMapRates type={type} />
      {type === 'rolloff' && <RentalSteps />}
      {type === 'rolloff' && <MaterialGuide />}
      {getFaqSection()}
      <CtaSection
        withBackground={type !== 'rolloff'}
        secondary={type === 'rolloff'}
      />
    </div>
  );
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dumpster-rentals',
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    type: DumpsterType;
  } => ({
    type: getDumpsterType(search.type),
  }),
  loader: () => {
    const searchParams = new URLSearchParams(window.location.search);
    return { type: getDumpsterType(searchParams.get('type')) };
  },
  component: DumpsterRentalsPage,
});
